import PMS from "@global/components/PMS";
import Requester from "@global/components/Requester";
import checkEmail from '@global/query/checkEmail';
import getCollaborator from '@global/query/getCollaborator';
import listCollaborator from '@global/query/listCollaborator';
import listPhaseCollaborator from '@global/query/listPhaseCollaborator';
import managerGetCollaborator from '@global/query/managerGetCollaborator';
import managerListCollaborator from '@global/query/managerListCollaborator';
import managerPhaseListCollaborator from '@global/query/managerListPhaseCollaborator';
import myProfile from '@global/query/myProfile';

const User = {
	getId: () => {
		return localStorage.getItem("_id");
	},
	setId: (_id) => {
		localStorage.setItem("_id", _id);
		return;
	},
	getEmail: () => {
		return localStorage.getItem("email");
	},
	setEmail: (email) => {
		localStorage.setItem("email", email);
		return;
	},
	canCreateObjectives: async () => {
		// Check if user is in definition phase
		const isInDefinitionPhase = PMS.Flows.verify(PMS.Flows.definition);

		if (isInDefinitionPhase) return true;

		// If not in definition phase, check if hired before October 1st
		try {
			const userData = await User.getUserQuery("profile {hired_date}");
			if (!userData || !userData.profile || !userData.profile.hired_date) return false;

			const hiredDate = new Date(userData.profile.hired_date);
			const currentYear = new Date().getFullYear();
			const cutoffDate = new Date(currentYear, 9, 1); // Month is 0-based, so 9 = October

			return hiredDate < cutoffDate;
		} catch (err) {
			console.error("Error checking if user can create objectives:", err);
			return false;
		}
	},
	getUserQuery: async(query, profile, id) => {
		try{
			let action = myProfile;

			if(PMS.Cognito.isHR()){
				action = getCollaborator;
			}
			else if(PMS.Cognito.isManager() && profile !== "myProfile"){
				action = managerGetCollaborator;
			}

			let res = await Requester({
				data: {
					query: action(query),
					variables: {
						id: id || PMS.User.getId()
					}
				}
			});

			return res.data.getCollaborator || res.data.myProfile || res.data.getManagerCollaborator;
		}
		catch(err){
			return {};
		}
	},

	listPhaseUsersQuery: async(query) => {
		try{
			if(PMS.Cognito.isHR()){
				let isEnding = false;
				let users = [];
				let page = 0;

				while(!isEnding){
					let res = await Requester({
						data: {
							query: listPhaseCollaborator(query),
							variables: {
								page,
								size: 50
							}
						}
					});
					users.push(...res.data.listPhaseCollaborator.collaborators);
					isEnding = res.data.listPhaseCollaborator.isEnding;
					page++;
				}

				return users.filter((item) => {
					if(item.profile && item.profile.current){
						return item.profile.current.status;
					}
					else{
						return item;
					}
				});
			}
			else if(PMS.Cognito.isManager()){
				let res = await Requester({
					data: {
						query: managerPhaseListCollaborator(query),
					}
				});

				return res.data.managerListPhaseCollaborator.collaborators;
			}
			else{
				return [];
			}
		}
		catch(err){
			return {};
		}
	},
	listUsersQuery: async(query, options = {}) => {
		try{
			if(PMS.Cognito.isHR()){
				let isEnding = false;
				let users = [];
				let page = 0;

				while(!isEnding){
					let res = await Requester({
						data: {
							query: listCollaborator(query),
							variables: {
								page,
								size: 100
							}
						}
					});
					users.push(...res.data.listCollaborator.collaborators);
					isEnding = res.data.listCollaborator.isEnding;
					page++;
				}

				if(options.includeInactive) return users;

				return users.filter((item) => {
					if(item.profile && item.profile.current){
						return item.profile.current.status;
					}
					else{
						return item;
					}
				});
			}
			else if(PMS.Cognito.isManager()){
				let res = await Requester({
					data: {
						query: managerListCollaborator(query),
					}
				});

				return res.data.listManagerCollaborator;
			}
			else{
				return [];
			}
		}
		catch(err){
			return {};
		}
	},
	checkEmailAvailability: async(email) => {
		let res = await Requester({
			data: {
				query: checkEmail(),
				variables: {email}
			}
		});

		return res.data.isEmailAvailable;
	},
	canManagerCreateObjectivesForCollaborator: async (collaboratorId) => {
		try {
			// Get the collaborator data
			const collaboratorData = await User.getUserQuery(
				"profile {current {hiring_date}} performance {objectives {year, objectives{objective}}}",
				"getCollaborator",
				collaboratorId
			);

			if (!collaboratorData || !collaboratorData.profile || !collaboratorData.profile.current || !collaboratorData.profile.current.hiring_date) {
				return false;
			}

			// Check if the collaborator was hired within the last MAX_DAYS_FOR_OBJECTIVES days
			const hiringDate = new Date(collaboratorData.profile.current.hiring_date);
			const today = new Date();
			const daysSinceHiring = Math.floor((today - hiringDate) / (1000 * 60 * 60 * 24));

			// Get the maximum days from environment variable or use default (15)
			const maxDays = parseInt(process.env.REACT_APP_MAX_DAYS_FOR_OBJECTIVES || "15");

			// Check if there are no objectives for the current year
			const currentYear = new Date().getFullYear();
			const hasObjectivesForCurrentYear = collaboratorData.performance && 
				collaboratorData.performance.objectives && 
				collaboratorData.performance.objectives.some(obj => 
					obj.year === currentYear && obj.objectives && obj.objectives.length > 0
				);

			// Return true if the collaborator was hired within the maximum days and has no objectives for the current year
			return daysSinceHiring <= maxDays && !hasObjectivesForCurrentYear;
		} catch (err) {
			console.error("Error checking if manager can create objectives for collaborator:", err);
			return false;
		}
	},
}

export default User;
